import { useWatch } from "react-hook-form";
import { COUNTRY_LABELS, joinFullName } from "@nh/shared";
import {
  CountryField,
  EmailField,
  FirstNameField,
  LastNameField,
  PasswordField,
  PhoneField,
  SummaryList,
  type SummaryItem,
} from "@/shared/ui/core";
import { TermsCheckbox } from "../components/TermsCheckbox";
import {
  STEP_ACCESS,
  STEP_IDENTITY,
  type RegisterFormValues,
} from "./registerSteps";

/**
 * Los cuerpos de los tres pasos del alta.
 *
 * Son componentes y no bloques de JSX dentro de `RegisterPage` por dos
 * razones concretas: cada uno necesita SU propia suscripción al formulario
 * (`useWatch` del país en el primero, de todo lo capturado en el tercero) y
 * un hook no se puede llamar dentro de un `if`. Además, así el paso que no
 * se ve no se suscribe a nada.
 *
 * Los tres se pintan DENTRO de la misma columna de `RegisterPage`, así que
 * devuelven campos sueltos —no un contenedor propio—: quien manda en el
 * ritmo de la card es la columna, no el paso.
 *
 * UNA columna, en cualquier tamaño de pantalla. Repartir los campos en dos
 * anularía la razón de ser del wizard: se partió el alta en pasos
 * precisamente para no tener que apretar el formulario a lo ancho.
 */

/** Paso 1 · Quién eres. */
export function IdentityStep() {
  /*
   * El país decide con qué prefijo se completan los diez dígitos
   * nacionales del teléfono. Se observa en vivo —y no se lee al enviar—
   * porque quien elige Estados Unidos y escribe sus diez dígitos debe
   * acabar en `+1` en ese mismo momento, no descubrir en el resumen que
   * su teléfono se volvió mexicano.
   */
  const country = useWatch<RegisterFormValues, "country">({ name: "country" });

  return (
    <>
      {/*
        `autoFocus` solo aquí: es el primer campo de la pantalla al
        cargarla. Al volver a este paso desde el dos, el foco lo maneja
        `RegisterPage`, que sabe si hay un error que atender primero.
      */}
      <FirstNameField autoFocus required />
      <LastNameField required />
      <CountryField required />
      <PhoneField required country={country || undefined} />
    </>
  );
}

/** Paso 2 · Tu acceso. */
export function AccessStep() {
  return (
    <>
      <EmailField purpose="account" required />

      {/*
        El medidor va en la contraseña y NO en confirmar: ahí no se elige
        nada, se copia lo de arriba, y un segundo medidor idéntico duplica
        cuatro renglones de requisitos por nada.
      */}
      <PasswordField
        purpose="new"
        hint="Mínimo 10 caracteres, con letra y número."
        showStrength
        required
      />

      <PasswordField
        name="confirmPassword"
        purpose="new"
        label="Confirmar contraseña"
        required
      />
    </>
  );
}

export interface ConfirmStepProps {
  /** Llevar de vuelta a un paso a corregir un dato del resumen. */
  onEdit: (step: number) => void;
}

/** Paso 3 · Confirmación. */
export function ConfirmStep({ onEdit }: ConfirmStepProps) {
  const values = useWatch<RegisterFormValues>() as RegisterFormValues;

  const items: SummaryItem[] = [
    {
      label: "Nombre",
      // El nombre para mostrar se DERIVA de las dos partes con la misma
      // función que usa el servidor. Componerlo aquí con un espacio a mano
      // sería una segunda regla que puede divergir de la del contrato.
      value: joinFullName(values.firstName, values.lastName) || EMPTY,
      onEdit: () => onEdit(STEP_IDENTITY),
    },
    {
      label: "Correo",
      value: values.email || EMPTY,
      onEdit: () => onEdit(STEP_ACCESS),
    },
    {
      label: "Teléfono",
      value: values.phone || EMPTY,
      onEdit: () => onEdit(STEP_IDENTITY),
    },
    {
      label: "País",
      value: values.country ? COUNTRY_LABELS[values.country] : EMPTY,
      onEdit: () => onEdit(STEP_IDENTITY),
    },
  ];

  return (
    <>
      <SummaryList items={items} />
      <TermsCheckbox />
    </>
  );
}

/** Lo que se pinta cuando un dato falta. Nunca debería verse. */
const EMPTY = "—";
